/**
 * Curriculum Planner — Programs List
 *
 * List/create programs anchored in the academic hierarchy.
 * Route: /kcis/admin/university/curriculum-planner/programs
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import KcisAdminShell, { KCIS_ADMIN_PALETTE as PAL } from "@/pages/kcis_admin/KcisAdminShell";
import { curriculumApi } from "@/lib/curriculumApi";

const CARD = { background: PAL.panel, border: `1px solid ${PAL.border}`, borderRadius: 14 };

export default function ProgramList() {
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({
    name: "", credential: "Certification", college: "",
    school: "", department: "", required_courses_count: 24,
    description: "", summary: "",
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await curriculumApi.listPrograms();
      setItems(d.items || []);
    } catch (e) { toast.error("Failed to load"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name.trim()) { toast.error("Program name is required"); return; }
    setSaving(true);
    try {
      await curriculumApi.createProgram({
        ...form,
        required_courses_count: Number(form.required_courses_count) || 0,
      });
      toast.success("Program created");
      setShowNew(false);
      setForm({ name: "", credential: "Certification", college: "", school: "",
                 department: "", required_courses_count: 24, description: "", summary: "" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setSaving(false); }
  };

  return (
    <KcisAdminShell>
      <div className="p-6 space-y-5"
           style={{ background: PAL.bg, color: PAL.text, minHeight: "100vh" }}
           data-testid="programs-list-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-widest" style={{ color: PAL.muted }}>
              KCIS Admin · Curriculum Planner
            </div>
            <h1 className="text-3xl font-semibold mt-1">Programs</h1>
            <p className="text-sm mt-1 max-w-2xl" style={{ color: PAL.muted }}>
              Every credential offered by KISSED University lives here. Add colleges, schools, departments, and programs before planning courses.
            </p>
          </div>
          <div className="flex gap-2">
            <Link to="/kcis/admin/university/curriculum-planner"
                  className="px-3 py-2 rounded-lg text-sm"
                  style={{ background: PAL.accentSoft, color: PAL.text }}
                  data-testid="back-executive-btn">← Executive Dashboard</Link>
            <button onClick={() => setShowNew((s) => !s)}
                    className="px-3 py-2 rounded-lg text-sm font-medium"
                    style={{ background: PAL.accent, color: "#12091E" }}
                    data-testid="new-program-btn">
              {showNew ? "Cancel" : "+ New Program"}
            </button>
          </div>
        </div>

        {showNew && (
          <div style={{ ...CARD, padding: 16 }} data-testid="new-program-form">
            <div className="text-sm font-medium mb-3">New Program</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Program name" value={form.name} onChange={(v) => setForm(f => ({ ...f, name: v }))} testid="new-program-name" />
              <Field label="Credential" value={form.credential} onChange={(v) => setForm(f => ({ ...f, credential: v }))} testid="new-program-credential" />
              <Field label="College" value={form.college} onChange={(v) => setForm(f => ({ ...f, college: v }))} testid="new-program-college" />
              <Field label="School (optional)" value={form.school} onChange={(v) => setForm(f => ({ ...f, school: v }))} testid="new-program-school" />
              <Field label="Department (optional)" value={form.department} onChange={(v) => setForm(f => ({ ...f, department: v }))} testid="new-program-department" />
              <Field label="Required courses" type="number" value={form.required_courses_count}
                      onChange={(v) => setForm(f => ({ ...f, required_courses_count: v }))}
                      testid="new-program-required" />
              <Field label="Summary (short)" value={form.summary} onChange={(v) => setForm(f => ({ ...f, summary: v }))}
                     className="md:col-span-2" testid="new-program-summary" />
              <Field label="Description" value={form.description} onChange={(v) => setForm(f => ({ ...f, description: v }))}
                     className="md:col-span-2" area testid="new-program-description" />
            </div>
            <div className="mt-3 flex gap-2">
              <button onClick={create} disabled={saving}
                      className="px-3 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
                      style={{ background: PAL.accent, color: "#12091E" }}
                      data-testid="save-new-program-btn">
                {saving ? "Saving…" : "Create program"}
              </button>
            </div>
          </div>
        )}

        <div style={{ ...CARD, padding: 0, overflow: "hidden" }}>
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: "#211A2B", color: PAL.muted }}>
                <th className="text-left px-4 py-2">Program</th>
                <th className="text-left px-4 py-2">College · School · Dept</th>
                <th className="text-right px-4 py-2">Courses</th>
                <th className="text-left px-4 py-2">Credential</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-6 text-center"
                        style={{ color: PAL.muted }} data-testid="programs-empty">
                  No programs yet.
                </td></tr>
              )}
              {items.map((p) => (
                <tr key={p.id} className="border-t"
                    style={{ borderColor: PAL.border }} data-testid={`program-row-${p.id}`}>
                  <td className="px-4 py-3 font-medium">{p.name}</td>
                  <td className="px-4 py-3" style={{ color: PAL.muted }}>
                    {[p.college, p.school, p.department].filter(Boolean).join(" · ")}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {p.course_count || 0}{p.required_courses_count ? ` / ${p.required_courses_count}` : ""}
                  </td>
                  <td className="px-4 py-3">{p.credential}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-1 rounded-md text-[11px] uppercase tracking-widest"
                           style={{ background: PAL.accentSoft }}>{p.status}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/kcis/admin/university/curriculum-planner/programs/${p.id}`}
                          className="text-sm hover:underline" style={{ color: PAL.accent }}
                          data-testid={`open-program-${p.id}`}>Open →</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </KcisAdminShell>
  );
}

function Field({ label, value, onChange, type = "text", area, testid, className = "" }) {
  return (
    <label className={`text-xs ${className}`} style={{ color: PAL.muted }}>
      <div className="mb-1">{label}</div>
      {area ? (
        <textarea rows={3} value={value} onChange={(e) => onChange(e.target.value)}
                   data-testid={testid}
                   className="w-full px-2 py-1 rounded-md text-sm"
                   style={{ background: PAL.bg, border: `1px solid ${PAL.border}`, color: PAL.text }} />
      ) : (
        <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
                className="w-full px-2 py-1 rounded-md text-sm"
                style={{ background: PAL.bg, border: `1px solid ${PAL.border}`, color: PAL.text }} />
      )}
    </label>
  );
}
